import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FramerButton } from "@/components/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreditLimitRequest, CreditLimitRequestStatus } from "@/graphql/__generated__/graphql-operations";
import { ChangeEvent, Dispatch, FormEvent, SetStateAction, useState } from "react";
import { updateAdminState } from "@/cache/appstate/WriteQueries";
import { useAdminPageState } from "@/hooks/cache/appState";
import { useEditCreditLimitRequestMutationHook } from "@/hooks/mutations";
import { AdminPageState } from "@/types";
import { AdminPageModals } from "@/constants/enums";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { handleValidation } from "@/utils/form";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type Props = {
  showModal: AdminPageModals;
  setShowModal: Dispatch<SetStateAction<AdminPageModals>>;
  creditLimiRequest: CreditLimitRequest;
}

function CreditLimitAdmin({ showModal, setShowModal, creditLimiRequest }: Props) {

  // States
  const [hasErrors, setHasErrors] = useState(false);
  const [errors, setErrors] = useState<{
    requestedAmount: boolean;
    reasonForRequest: boolean;
    reasonForDenial: boolean;
  }>({
    requestedAmount: false,
    reasonForRequest: false,
    reasonForDenial: false,
  });

  // Hooks
  const state: AdminPageState = useAdminPageState();
  const { t } = useTranslation();

  // Mutation hooks
  const { editCreditLimitRequestMutation } = useEditCreditLimitRequestMutationHook();

  /**
  * onChange handler for input fields
  * @param {object} event - input event
  */
  function handleOnChange(event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>, type: string) {
    // get the updated user input
    const { name, value } = event.target;
    const isValid = handleValidation(value, type);

    if(isValid) {
      updateAdminState({...state, creditLimitRequestInput: {[name]: value}});
      setHasErrors(false);
    }
    else {
      setHasErrors(true);
    }
  }

  /**
   * Handler function for select value changes
   * @param {string} value - The selected value
   * @param {string} name - The field name
   */
  function handleSelectChange(value: string, name: string) {
    const isValid = handleValidation(value, name);

    if(isValid) {
      updateAdminState({...state, creditLimitRequestInput: {[name]: value}});
      setHasErrors(false);
      setErrors({...errors, [name]: false});
    }
    else {
      setErrors({...errors, [name]: true});
      setHasErrors(true);
    }
  }

  /**
   * Handler function for editCreditLimitRequestMutation
   * @param {FormEvent<Element>} event
   */
  function handleSubmitResponse(event: FormEvent<Element>) {
    event.preventDefault();
    editCreditLimitRequestMutation({
      variables: {
        id: creditLimiRequest?._id ?? "",
        creditLimitRequestInput: {...state.creditLimitRequestInput}
      },
    }).then(() => setShowModal(AdminPageModals.NoModal));
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowModal(AdminPageModals.NoModal);
    }
  };

  return (
    <Dialog open={showModal === AdminPageModals.CLRDetails} onOpenChange={handleOpenChange}>
      <DialogContent className="px-6 max-w-3xl max-h-[90vh] overflow-y-auto dark:bg-mfdarklight"
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg dark:text-white">Request Details</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmitResponse}>
          <div className="flex-1 pt-2 overflow-y-auto p-1">
            <div className="flex flex-col select-none">
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  name="status"
                  defaultValue={creditLimiRequest.status}
                  onValueChange={(value) => handleSelectChange(value, "status")}
                >
                  <SelectTrigger className="dark:bg-mfdarklight mb-2">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-mfdarklight">
                    {[
                      CreditLimitRequestStatus.Approved,
                      CreditLimitRequestStatus.Pending,
                      CreditLimitRequestStatus.Denied
                    ].map((statusEnum) => (
                      <SelectItem
                        key={statusEnum}
                        value={statusEnum}
                        className="dark:hover:bg-mforange hover:bg-mforange"
                      >
                        {statusEnum}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="requestedAmount">{t("customeroverview.labels.requestedAmount")}</Label>
                <Input
                  className={`${errors.requestedAmount ? "border-red-500 focus:border-red-500" : ""}`}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChange(event, "number")}
                  placeholder={t("customeroverview.labels.requestedAmount").toString()}
                  name="requestedAmount"
                  type="number"
                  defaultValue={creditLimiRequest.requestedAmount}
                  required
                />
                {errors.requestedAmount && <p className="text-red-500">{t("customeroverview.errorMessages.amountInvalid")}</p>}
              </div>
              <div>
                <Label htmlFor="reasonForRequest">{t("customeroverview.labels.reasonForReq")}</Label>
                <Textarea
                  className={`${errors.reasonForRequest ? "border-red-500 focus:border-red-500" : ""}`}
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>) => handleOnChange(event, "text")}
                  placeholder={t("customeroverview.labels.reasonForReq").toString()}
                  name="reasonForRequest"
                  defaultValue={creditLimiRequest.reasonForRequest}
                  required={false}
                />
                {errors.reasonForRequest && <p className="text-red-500">{t("customeroverview.errorMessages.reasonInvalid")}</p>}
              </div>
              <div>
                <Label htmlFor="reasonForDenial">Reason for Denial</Label>
                <Textarea
                  className={`${errors.reasonForDenial ? "border-red-500 focus:border-red-500" : ""}`}
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>) => handleOnChange(event, "text")}
                  placeholder={"Reason for denial"}
                  name="reasonForDenial"
                  defaultValue={creditLimiRequest.reasonForDenial as string}
                  required={false}
                />
                {errors.reasonForDenial && <p className="text-red-500">{t("customeroverview.errorMessages.reasonInvalid")}</p>}
              </div>
            </div>
          </div>

          <DialogFooter>
            <div className="flex justify-end w-full mt-4">
              <FramerButton
                disabled={hasErrors}
                className="bg-mforange text-white hover:bg-mforange/80"
              >
                {t("common:buttons.submit")}
              </FramerButton>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CreditLimitAdmin;
